const reclaimReferralCodeIntakeStatuses = [
  "applied",
  "skipped_free_terms",
  "skipped_without_code",
  "unavailable",
] as const;

type ReclaimReferralCodeIntakeStatus =
  (typeof reclaimReferralCodeIntakeStatuses)[number];

export const RECLAIM_REFERRAL_CODE_STATUS_ANSWER_ID =
  "reclaimPartnerCodeStatus";
export const RECLAIM_REFERRAL_CODE_ANSWER_ID = "reclaimPartnerCode";
export const RECLAIM_REFERRAL_CODE_REFERRER_NAME_ANSWER_ID =
  "reclaimPartnerCodePartnerName";
export const RECLAIM_REFERRAL_CODE_REFERRER_PROFILE_IMAGE_URL_ANSWER_ID =
  "reclaimPartnerCodePartnerProfileImageUrl";

interface ReclaimReferralCodeIntakeState {
  referrerName?: string;
  referrerProfileImageUrl?: string;
  referralCode?: string;
  status?: ReclaimReferralCodeIntakeStatus;
}

export function buildReclaimReferralCodeIntakeAnswers({
  referrerName,
  referrerProfileImageUrl,
  referralCode,
  status,
}: ReclaimReferralCodeIntakeState & {
  status: ReclaimReferralCodeIntakeStatus;
}): Record<string, string> {
  const normalizedReferralCode = referralCode?.trim().toUpperCase() ?? "";
  const normalizedReferrerName = referrerName?.trim() ?? "";
  const normalizedReferrerProfileImageUrl =
    referrerProfileImageUrl?.trim() ?? "";
  return {
    [RECLAIM_REFERRAL_CODE_STATUS_ANSWER_ID]: status,
    [RECLAIM_REFERRAL_CODE_ANSWER_ID]: normalizedReferralCode,
    [RECLAIM_REFERRAL_CODE_REFERRER_NAME_ANSWER_ID]: normalizedReferrerName,
    [RECLAIM_REFERRAL_CODE_REFERRER_PROFILE_IMAGE_URL_ANSWER_ID]:
      normalizedReferrerProfileImageUrl,
  };
}

export function getReclaimReferralCodeIntakeState(
  answers: Record<string, unknown> | null | undefined,
): ReclaimReferralCodeIntakeState {
  const status = parseStatus(answers?.[RECLAIM_REFERRAL_CODE_STATUS_ANSWER_ID]);
  const referralCode = parseString(answers?.[RECLAIM_REFERRAL_CODE_ANSWER_ID]);
  const referrerName = parseString(
    answers?.[RECLAIM_REFERRAL_CODE_REFERRER_NAME_ANSWER_ID],
  );
  const referrerProfileImageUrl = parseString(
    answers?.[RECLAIM_REFERRAL_CODE_REFERRER_PROFILE_IMAGE_URL_ANSWER_ID],
  );
  return {
    ...(status ? { status } : {}),
    ...(referralCode ? { referralCode } : {}),
    ...(referrerName ? { referrerName } : {}),
    ...(referrerProfileImageUrl ? { referrerProfileImageUrl } : {}),
  };
}

export function hasReclaimReferralCodeIntakeOutcome(
  answers: Record<string, unknown> | null | undefined,
): boolean {
  return !!getReclaimReferralCodeIntakeState(answers).status;
}

export function isReclaimReferralCodeIntakeComplete(
  answers: Record<string, unknown> | null | undefined,
): boolean {
  const status = getReclaimReferralCodeIntakeState(answers).status;
  return (
    status === "applied" ||
    status === "skipped_free_terms" ||
    status === "skipped_without_code"
  );
}

function parseString(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
}

function parseStatus(
  value: unknown,
): ReclaimReferralCodeIntakeStatus | undefined {
  return typeof value === "string" &&
    (reclaimReferralCodeIntakeStatuses as readonly string[]).includes(value)
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      (value as ReclaimReferralCodeIntakeStatus)
    : undefined;
}
